import type { ILashingBridgeGeometry } from "./getLashingBridgesGeometry";
import { LASHING_BRIDGE_PART_CLEARANCE_IN_MM } from "../consts";

export interface IVesselPartSpan {
  /** Aft end of the part, in mm */
  aftLcg: number;
  /** Fore end of the part, in mm */
  fwdLcg: number;
}

/**
 * Span of a vessel part (deckhouse, smokestack, crane...) to draw, clear of the lashing bridges
 * standing in it. A part fills the space after its fore bay, and a bridge next to a part stands off
 * the end of its bay (see `getDefaultLashingBridgeLcg`), so both claim the same space.
 *
 * Each bridge whose footprint (`lcgCenter ± thickness / 2`, plus the clearance) overlaps the part
 * pulls back the nearest end of the part, past the bridge and the clearance. The part keeps its
 * span when the bridges would leave it shorter than `minLenRatio` of its length.
 * @returns The span to draw (the same values as `part` when no bridge overlaps it)
 */
export function getVesselPartClearSpan(
  part: IVesselPartSpan,
  geometries: Pick<ILashingBridgeGeometry, "lcgCenter" | "thickness">[],
  {
    clearanceInMm = LASHING_BRIDGE_PART_CLEARANCE_IN_MM,
    minLenRatio = 0.5,
  }: {
    /** Defaults to {@link LASHING_BRIDGE_PART_CLEARANCE_IN_MM} */
    clearanceInMm?: number;
    /** Shortest clear span, as a ratio of the part length. Defaults to 0.5 */
    minLenRatio?: number;
  } = {},
): IVesselPartSpan {
  const len = part.fwdLcg - part.aftLcg;
  if (!(len > 0)) return { aftLcg: part.aftLcg, fwdLcg: part.fwdLcg };

  const mid = (part.aftLcg + part.fwdLcg) * 0.5;
  let { aftLcg, fwdLcg } = part;

  geometries.forEach((g) => {
    const lo = g.lcgCenter - g.thickness * 0.5 - clearanceInMm;
    const hi = g.lcgCenter + g.thickness * 0.5 + clearanceInMm;
    if (hi <= part.aftLcg || lo >= part.fwdLcg) return;

    if (g.lcgCenter >= mid) fwdLcg = Math.min(fwdLcg, lo);
    else aftLcg = Math.max(aftLcg, hi);
  });

  return fwdLcg - aftLcg >= len * minLenRatio
    ? { aftLcg, fwdLcg }
    : { aftLcg: part.aftLcg, fwdLcg: part.fwdLcg };
}
